import ExcelJS from "exceljs";
import { removeSampleTestFromPayload } from "../../excel/mapping/sampleDataFilter";
import { serializeTaxMaintenancesForExport } from "../../constants/TaxMaintenance";
import { resolveRoomDefinitionListOrder } from "../../excel/mapping/roomDefinitionRows";
import { SettingsGroup } from "./settingsGroups";

export type { SettingsGroup } from "./settingsGroups";
export { getSettingsGroups } from "./settingsGroups";

export interface DataUtilityImportItem {
  importData: string; // JSON.stringify({ [sheetName]: rows[] })
  totalRows: number;
  excelSheetName: string;
}

// ---------------------------------------------------------------------------
// Field rename maps: UI field name → API field name (per screen)
// ---------------------------------------------------------------------------
const FIELD_RENAMES: Record<string, Record<string, string>> = {
  "Payment Method": {
    isUseSpecialGuarentee: "isUseSpecialGuarantee",
  },
  "Post Type": {
    groupingFunctionality: "groupFunctionality",
    isRequiredReference: "isRequireReference",
    extractRevenueBracket: "extractRevenueBucket",
    isCommisionable: "isCommissionable",
    isCommissionPecentage: "isCommissionPercentage",
  },
  "Tax Post Type": {
    groupingFunctionality: "groupFunctionality",
    isRequiredReference: "isRequireReference",
    extractRevenueBracket: "extractRevenueBucket",
    isCommisionable: "isCommissionable",
    isCommissionPecentage: "isCommissionPercentage",
  },
  "Room Type Maintenance": {
    petCount: "noOfPets",
    minOccupancy: "minimumOccupancy",
    maxOccupancy: "maximumOccupancy",
  },
  "Room Definition": {
    condoRendedToOwner: "condoRentedToOwner",
  },
};

// ---------------------------------------------------------------------------
// Hardcoded defaults for fields missing from the UI (per screen)
// ---------------------------------------------------------------------------
const FIELD_DEFAULTS: Record<string, Record<string, unknown>> = {
  "Tax Exempt Categories": { isActive: true },
  Origins: { isActive: true },
  "Guest Type": { isActive: true, taxExemptCategories: "[]" },
  "Room Feature": { isActive: true, isCondoOnly: false },
  "Room Location": { isActive: true, isCondoOnly: false },
  Department: { isActive: true },
  "Segment 1": { isActive: true },
  Building: { isActive: true, taxExemptCategories: "[]" },
  "Segment 2": { isActive: true },
  "Post Type": {
    isActive: true,
    isCredit: false,
    isDeposit: false,
    allowEarn: false,
    defaultAmount: 0,
    floorLimit: 0,
    ceilingLimit: 0,
    groupFunctionality: "None",
    isRequireReference: false,
    isRestrictReservationPost: false,
    isRoomRevenue: false,
    isRequireComment: false,
    isPasscode: false,
    isFoodAndBevClassifier: false,
    defaultFolio: 1,
    defaultMasterFolio: 1,
    isCommissionable: false,
    commissionFlat: 0,
    isChargedToCondoOwner: false,
    isCommissionPercentage: false,
    commissionPercentage: 0,
    isGiftCard: false,
    giftCardType: "None",
    taxes: "[]",
  },
  "Tax Post Type": {
    isActive: true,
    isCredit: false,
    isDeposit: false,
    allowEarn: false,
    defaultAmount: 0,
    floorLimit: 0,
    ceilingLimit: 0,
    groupFunctionality: "None",
    isRequireReference: false,
    isRestrictReservationPost: false,
    isRoomRevenue: false,
    isRequireComment: false,
    isPasscode: false,
    isFoodAndBevClassifier: false,
    defaultFolio: 1,
    defaultMasterFolio: 1,
    isCommissionable: false,
    commissionFlat: 0,
    isChargedToCondoOwner: false,
    isCommissionPercentage: false,
    commissionPercentage: 0,
    isGiftCard: false,
    giftCardType: "None",
    taxes: "[]",
  },
  Tax: {
    exemptDays: 0,
    isTaxable: false,
    isPostWithRoom: false,
    isSpecializedUse: false,
    isGSTTax: false,
    isOccupancyTax: false,
    isOCCTaxNoOfRooms: false,
    isPostTaxToFirstFolio: false,
    isShowOnSplits: false,
    taxMaintenances: [],
    isActive: true,
  },
  "Room Type Maintenance": {
    noOfRooms: 0,
    maximumGuests: 0,
    maximumNights: 0,
    isBedded: true,
    isPerm: false,
    isUnblockPasscode: false,
    isBlockRequired: false,
    isAllowOversell: false,
    isBlockingReminder: false,
    isSuite: false,
    amount: 0,
    resortFeeType: 0,
    feeFrequency: 0,
    adult: false,
    youth: false,
    teen: false,
    child: false,
    todlers: false,
    isROH: false,
    countRoomAs: 1,
    isPetAllowed: false,
    noOfPets: 0,
    isNightly: true,
    isHourly: false,
    gapTime: 0,
    maximumOccupancy: 0,
    minimumOccupancy: 0,
    type: "None",
    isActive: true,
  },
  "HouseKeeping Sections": { isActive: true },
  "Payment Method": {
    paymentType: "Cash",
    isUseSpecialGuarantee: false,
    isActivateInterface: false,
    isRestrictPOSChanges: false,
    isMovieRestrictions: false,
    isInterfacePostToCheckoutFolio: false,
    isActive: true,
  },
  "Room Definition": {
    isSmoking: false,
    isCotNotAllowed: false,
    isCribNotAllowed: false,
    ownershipType: "None",
    isBedded: false,
    isNoAutoPostRoomAndTax: false,
    isSuite: false,
    beds: 1,
    blockingOrder: 0,
    connectingRoomCodes: "[]",
    suiteRoomCodes: "[]",
    condoAddress: "[]",
    condoContactDetails: "[]",
    roomStatus: "V",
    listOrder: 1,
    isActive: true,
  },
};

// ---------------------------------------------------------------------------
// API sheet key per screen name (matches the Excel sheet name / root node key)
// ---------------------------------------------------------------------------
export const SCREEN_SHEET_KEY: Record<string, string> = {
  Building: "Building",
  "Room Feature": "Room Feature",
  "Room Location": "Room Location",
  "HouseKeeping Sections": "HouseKeeping Sections",
  "Room Type Maintenance": "Room Type Maintenance",
  "Room Definition": "Room Definition",
  Department: "Department",
  Category: "Category",
  "Tax Exempt Categories": "Tax Exempt Categories",
  "Tax Post Type": "Post Type", // merged into same sheet as Post Type
  Tax: "Tax",
  "Post Type": "Post Type",
  "Guest Type": "Guest Type",
  Origins: "Origins",
  "Payment Method": "Payment Method",
  "Segment 1": "Segment 1",
  "Segment 2": "Segment 2",
  "Market Segment": "Market Segment",
};

// ---------------------------------------------------------------------------
// API field column order per screen (matches the Excel column order)
// ---------------------------------------------------------------------------
const SCREEN_COLUMNS: Record<string, string[]> = {
  Building: ["code", "name", "taxExemptCategories", "isActive"],
  "Room Feature": ["code", "name", "isActive", "isCondoOnly"],
  "Room Location": ["code", "name", "isActive", "isCondoOnly"],
  "HouseKeeping Sections": ["code", "name", "buildingCode", "isActive"],
  "Room Type Maintenance": [
    "code",
    "name",
    "buildingCode",
    "noOfRooms",
    "maximumGuests",
    "maximumNights",
    "isBedded",
    "isPerm",
    "isUnblockPasscode",
    "isBlockRequired",
    "isAllowOversell",
    "isBlockingReminder",
    "isSuite",
    "amount",
    "resortFeeType",
    "feeFrequency",
    "adult",
    "youth",
    "teen",
    "child",
    "todlers",
    "isROH",
    "villaHomeTemplateCode",
    "postTypeName",
    "countRoomAs",
    "isPetAllowed",
    "noOfPets",
    "isNightly",
    "isHourly",
    "gapTime",
    "maximumOccupancy",
    "minimumOccupancy",
    "type",
    "sortOrder",
    "isActive",
  ],
  "Room Definition": [
    "isSmoking",
    "isCotNotAllowed",
    "isCribNotAllowed",
    "ownershipType",
    "roomNumber",
    "roomTypeCode",
    "buildingCode",
    "isBedded",
    "description",
    "isNoAutoPostRoomAndTax",
    "isSuite",
    "beds",
    "roomsForOccupancyTax",
    "connectingRoomCodes",
    "suiteRoomCodes",
    "blockingOrder",
    "floor",
    "energySection",
    "keyNumber",
    "licenseFromDate",
    "licenseToDate",
    "physicalAddress",
    "stayOverPoints",
    "checkPoints",
    "lastDecorated",
    "mattressTurned",
    "housekeepingSection",
    "roomFeatures",
    "roomLocations",
    "roomStatus",
    "condoDescription",
    "condoFirstName",
    "condoLastName",
    "condoNightAvailable",
    "condoRentedToGuest",
    "condoRentedToOwner",
    "condoRoomRevenue",
    "condoUnitCondition",
    "condoAddress",
    "condoContactDetails",
    "listOrder",
    "isActive",
  ],
  Department: ["name", "isActive"],
  Category: ["name", "departmentName"],
  "Tax Exempt Categories": ["code", "name", "isActive"],
  "Post Type": [
    "postTypeNumber",
    "departmentName",
    "categoryName",
    "postTypeName",
    "buildingCode",
    "isActive",
    "isCredit",
    "isDeposit",
    "allowEarn",
    "characteristics",
    "postTypeCategory",
    "defaultAmount",
    "floorLimit",
    "ceilingLimit",
    "groupFunctionality",
    "isRequireReference",
    "isRestrictReservationPost",
    "isRoomRevenue",
    "isRequireComment",
    "isPasscode",
    "isFoodAndBevClassifier",
    "defaultFolio",
    "defaultMasterFolio",
    "glNumber",
    "glOffsetNumber",
    "sageExtractNumber",
    "extractRevenueBucket",
    "isCommissionable",
    "commissionFlat",
    "isChargedToCondoOwner",
    "isCommissionPercentage",
    "commissionPercentage",
    "isGiftCard",
    "giftCardType",
    "postTypeGrouping",
    "taxes",
  ],
  Tax: [
    "taxType",
    "postTypeName",
    "exemptDays",
    "effectiveDate",
    "pSTTax",
    "extractCode",
    "isTaxable",
    "isPostWithRoom",
    "isSpecializedUse",
    "isGSTTax",
    "isOccupancyTax",
    "isOCCTaxNoOfRooms",
    "isPostTaxToFirstFolio",
    "isShowOnSplits",
    "taxExemptCategory",
    "taxMaintenances",
    "isActive",
  ],
  "Guest Type": [
    "code",
    "name",
    "isRouteLocalCalls",
    "isTurndown",
    "isSkipResortFee",
    "creditCardPreAuth",
    "taxExemptCategories",
    "isActive",
  ],
  Origins: ["code", "name", "isActive", "isRouteLocalCalls"],
  "Payment Method": [
    "code",
    "name",
    "paymentType",
    "postTypeNumber",
    "isUseSpecialGuarantee",
    "creditLimit",
    "isActivateInterface",
    "isRestrictPOSChanges",
    "isMovieRestrictions",
    "isInterfacePostToCheckoutFolio",
    "centralResvEquivalent",
    "phoneRestrictions",
    "isActive",
  ],
  "Segment 1": [
    "code",
    "name",
    "isRouteLocalCalls",
    "guestTypeCode",
    "isActive",
  ],
  "Segment 2": [
    "code",
    "name",
    "isRouteLocalCalls",
    "parentSegment1Code",
    "guestTypeCode",
    "isActive",
  ],
  "Market Segment": ["code", "name", "washPercent", "isActive"],
};

// ---------------------------------------------------------------------------
// Allowed API fields per screen (whitelist derived from Excel rules).
// Only these fields will appear in the API payload — all others are stripped.
// ---------------------------------------------------------------------------
const SCREEN_ALLOWED_FIELDS: Record<string, Set<string>> = Object.fromEntries(
  Object.entries({
    Building: ["code", "name", "taxExemptCategories", "isActive"],
    "Room Feature": ["code", "name", "isActive", "isCondoOnly"],
    "Room Location": ["code", "name", "isActive", "isCondoOnly"],
    "HouseKeeping Sections": ["code", "name", "buildingCode", "isActive"],
    "Room Type Maintenance": [
      "code",
      "name",
      "buildingCode",
      "noOfRooms",
      "maximumGuests",
      "maximumNights",
      "isBedded",
      "isPerm",
      "isUnblockPasscode",
      "isBlockRequired",
      "isAllowOversell",
      "isBlockingReminder",
      "isSuite",
      "amount",
      "resortFeeType",
      "feeFrequency",
      "adult",
      "youth",
      "teen",
      "child",
      "todlers",
      "isROH",
      "villaHomeTemplateCode",
      "postTypeName",
      "countRoomAs",
      "isPetAllowed",
      "noOfPets",
      "isNightly",
      "isHourly",
      "gapTime",
      "maximumOccupancy",
      "minimumOccupancy",
      "type",
      "sortOrder",
      "isActive",
    ],
    "Room Definition": [
      "isSmoking",
      "isCotNotAllowed",
      "isCribNotAllowed",
      "ownershipType",
      "roomNumber",
      "roomTypeCode",
      "buildingCode",
      "isBedded",
      "description",
      "isNoAutoPostRoomAndTax",
      "isSuite",
      "beds",
      "roomsForOccupancyTax",
      "connectingRoomCodes",
      "suiteRoomCodes",
      "blockingOrder",
      "floor",
      "energySection",
      "keyNumber",
      "licenseFromDate",
      "licenseToDate",
      "physicalAddress",
      "stayOverPoints",
      "checkPoints",
      "lastDecorated",
      "mattressTurned",
      "housekeepingSection",
      "roomFeatures",
      "roomLocations",
      "roomStatus",
      "condoDescription",
      "condoFirstName",
      "condoLastName",
      "condoNightAvailable",
      "condoRentedToGuest",
      "condoRentedToOwner",
      "condoRoomRevenue",
      "condoUnitCondition",
      "condoAddress",
      "condoContactDetails",
      "listOrder",
      "isActive",
    ],
    Department: ["name", "isActive"],
    Category: ["name", "departmentName"],
    "Tax Exempt Categories": ["code", "name", "isActive"],
    "Tax Post Type": [
      "postTypeNumber",
      "departmentName",
      "categoryName",
      "postTypeName",
      "buildingCode",
      "isActive",
      "isCredit",
      "isDeposit",
      "allowEarn",
      "characteristics",
      "postTypeCategory",
      "defaultAmount",
      "floorLimit",
      "ceilingLimit",
      "groupFunctionality",
      "isRequireReference",
      "isRestrictReservationPost",
      "isRoomRevenue",
      "isRequireComment",
      "isPasscode",
      "isFoodAndBevClassifier",
      "defaultFolio",
      "defaultMasterFolio",
      "glNumber",
      "glOffsetNumber",
      "sageExtractNumber",
      "extractRevenueBucket",
      "isCommissionable",
      "commissionFlat",
      "isChargedToCondoOwner",
      "isCommissionPercentage",
      "commissionPercentage",
      "isGiftCard",
      "giftCardType",
      "postTypeGrouping",
      "taxes",
    ],
    Tax: [
      "taxType",
      "postTypeName",
      "exemptDays",
      "effectiveDate",
      "pSTTax",
      "extractCode",
      "isTaxable",
      "isPostWithRoom",
      "isSpecializedUse",
      "isGSTTax",
      "isOccupancyTax",
      "isOCCTaxNoOfRooms",
      "isPostTaxToFirstFolio",
      "isShowOnSplits",
      "taxExemptCategory",
      "taxMaintenances",
      "isActive",
    ],
    "Post Type": [
      "postTypeNumber",
      "departmentName",
      "categoryName",
      "postTypeName",
      "buildingCode",
      "isActive",
      "isCredit",
      "isDeposit",
      "allowEarn",
      "characteristics",
      "postTypeCategory",
      "defaultAmount",
      "floorLimit",
      "ceilingLimit",
      "groupFunctionality",
      "isRequireReference",
      "isRestrictReservationPost",
      "isRoomRevenue",
      "isRequireComment",
      "isPasscode",
      "isFoodAndBevClassifier",
      "defaultFolio",
      "defaultMasterFolio",
      "glNumber",
      "glOffsetNumber",
      "sageExtractNumber",
      "extractRevenueBucket",
      "isCommissionable",
      "commissionFlat",
      "isChargedToCondoOwner",
      "isCommissionPercentage",
      "commissionPercentage",
      "isGiftCard",
      "giftCardType",
      "postTypeGrouping",
      "taxes",
    ],
    "Guest Type": [
      "code",
      "name",
      "isRouteLocalCalls",
      "isTurndown",
      "isSkipResortFee",
      "creditCardPreAuth",
      "taxExemptCategories",
      "isActive",
    ],
    Origins: ["code", "name", "isActive", "isRouteLocalCalls"],
    "Payment Method": [
      "code",
      "name",
      "paymentType",
      "postTypeNumber",
      "isUseSpecialGuarantee",
      "creditLimit",
      "isActivateInterface",
      "isRestrictPOSChanges",
      "isMovieRestrictions",
      "isInterfacePostToCheckoutFolio",
      "centralResvEquivalent",
      "phoneRestrictions",
      "isActive",
    ],
    "Segment 1": [
      "code",
      "name",
      "isRouteLocalCalls",
      "guestTypeCode",
      "isActive",
    ],
    "Segment 2": [
      "code",
      "name",
      "isRouteLocalCalls",
      "parentSegment1Code",
      "guestTypeCode",
      "isActive",
    ],
    "Market Segment": ["code", "name", "washPercent", "isActive"],
  }).map(([k, v]) => [k, new Set(v as string[])]),
);

// isRouteLocalCalls: UI uses snake_case, API uses PascalCase
const ROUTE_LOCAL_CALLS_MAP: Record<string, string> = {
  no_routing: "NoRouting",
  route_local_calls: "RouteLocalCalls",
  NoRouting: "NoRouting",
  RouteLocalCalls: "RouteLocalCalls",
};

// Fields whose array values should extract a specific property from each item
const ARRAY_EXTRACT_FIELDS: Record<string, string> = {
  taxExemptCategories: "code",
  roomFeatures: "code",
  roomLocations: "code",
  taxes: "taxType",
};

// Single-value reference fields — UI sends "CODE - Name" string or object → extract code
const CODE_REF_FIELDS = new Set([
  "guestTypeCode",
  "parentSegment1Code",
  "taxExemptCategory",
  "buildingCode",
  "roomTypeCode",
  "housekeepingSection",
]);

// Fields where UI sends "NUMBER - Name" → extract the integer
const NUMBER_REF_FIELDS = new Set(["postTypeNumber"]);

// Fields where UI sends "NUMBER - postTypeName" → extract name after " - "
const NAME_AFTER_DASH_FIELDS = new Set(["postTypeName"]);

// Fields that are date type → format as yyyy-MM-dd
const DATE_FIELDS = new Set([
  "effectiveDate",
  "licenseFromDate",
  "licenseToDate",
  "lastDecorated",
  "mattressTurned",
]);

// ---------------------------------------------------------------------------
// Extract code from "CODE - Name" string or object
// ---------------------------------------------------------------------------
const extractCode = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") {
    const idx = value.indexOf(" - ");
    return idx >= 0 ? value.substring(0, idx).trim() : value.trim();
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return String((value as Record<string, unknown>).code ?? "");
  }
  return String(value);
};

// ---------------------------------------------------------------------------
// Map a single UI row → API row (apply renames + inject defaults)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Field type classification directly from Field Rules (Type column)
// ---------------------------------------------------------------------------

// Fields that are Boolean type → serialize as "True"/"False"
const BOOLEAN_FIELDS = new Set([
  // Tax Exempt Categories
  "isActive",
  // Origins, Guest Type, Room Feature, Room Location, Building, Segment 1/2, HouseKeeping, etc.
  "isCondoOnly",
  "isTurndown",
  "isSkipResortFee",
  // Post Type / Tax Post Type
  "isCredit",
  "isDeposit",
  "allowEarn",
  "isRequireReference",
  "isRestrictReservationPost",
  "isRoomRevenue",
  "isRequireComment",
  "isPasscode",
  "isFoodAndBevClassifier",
  "isCommissionable",
  "isChargedToCondoOwner",
  "isCommissionPercentage",
  "isGiftCard",
  // Tax
  "isTaxable",
  "isPostWithRoom",
  "isSpecializedUse",
  "isGSTTax",
  "isOccupancyTax",
  "isOCCTaxNoOfRooms",
  "isPostTaxToFirstFolio",
  "isShowOnSplits",
  // Room Type Maintenance
  "isBedded",
  "isPerm",
  "isUnblockPasscode",
  "isBlockRequired",
  "isAllowOversell",
  "isBlockingReminder",
  "isSuite",
  "adult",
  "youth",
  "teen",
  "child",
  "todlers",
  "isROH",
  "isPetAllowed",
  "isNightly",
  "isHourly",
  // Payment Method
  "isUseSpecialGuarantee",
  "isActivateInterface",
  "isRestrictPOSChanges",
  "isMovieRestrictions",
  "isInterfacePostToCheckoutFolio",
  // Room Definition
  "isSmoking",
  "isCotNotAllowed",
  "isCribNotAllowed",
  "isNoAutoPostRoomAndTax",
]);

// Fields that are int type → serialize as integer string
const INT_FIELDS = new Set([
  "postTypeNumber",
  "defaultFolio",
  "defaultMasterFolio",
  "commissionFlat",
  "commissionPercentage",
  "exemptDays",
  "countRoomAs",
  "noOfPets",
  "gapTime",
  "maximumOccupancy",
  "minimumOccupancy",
  "noOfRooms",
  "maximumGuests",
  "maximumNights",
  "resortFeeType",
  "beds",
  "blockingOrder",
  "listOrder",
  "stayOverPoints",
  "checkPoints",
  "sortOrder",
]);

// Fields that are decimal type → serialize as decimal string
const DECIMAL_FIELDS = new Set([
  "defaultAmount",
  "floorLimit",
  "ceilingLimit",
  "amount",
  "creditLimit",
]);

// Fields that are empty/string-enum type → empty string when falsy/0
const EMPTY_WHEN_FALSY_FIELDS = new Set([
  "terminalName",
  "glNumber",
  "glOffsetNumber",
  "sageExtractNumber",
  "extractRevenueBucket",
  "postTypeGrouping",
  "pSTTax",
  "extractCode",
  "villaHomeTemplateCode",
  "postTypeName",
  "creditLimit",
  "centralResvEquivalent",
  "floor",
  "energySection",
  "keyNumber",
  "physicalAddress",
  "housekeepingSection",
  "sortOrder",
  // condo string fields
  "condoDescription",
  "condoFirstName",
  "condoLastName",
  "condoNightAvailable",
  "condoRentedToGuest",
  "condoRentedToOwner",
  "condoRoomRevenue",
  "condoUnitCondition",
  "condoAddress",
  "condoContactDetails",
  // date fields when empty
  "lastDecorated",
  "mattressTurned",
]);

// feeFrequency numeric → label mapping (Field Rules: None 0, DayOfArrival 1, Daily 2)
const FEE_FREQUENCY_MAP: Record<string, string> = {
  "0": "None",
  None: "None",
  "1": "DayOfArrival",
  "2": "Daily",
};

const serializeValue = (value: unknown, apiKey?: string): unknown => {
  // taxMaintenances: array of objects → JSON string in one Excel/API cell
  if (apiKey === "taxMaintenances") {
    return serializeTaxMaintenancesForExport(value);
  }

  // villaHomeTemplateCode: strip placeholder
  if (apiKey === "villaHomeTemplateCode") {
    if (!value || String(value).toLowerCase().includes("villatemplate"))
      return "";
    return value;
  }

  // isRouteLocalCalls: map snake_case/empty → PascalCase or empty
  if (apiKey === "isRouteLocalCalls") {
    if (
      !value ||
      value === false ||
      String(value).toLowerCase() === "false" ||
      value === 0
    )
      return "";
    const mapped = ROUTE_LOCAL_CALLS_MAP[String(value)];
    return mapped ?? String(value);
  }

  // ownershipType: "None 0, Condo 1" → string label (Field Rules)
  if (apiKey === "ownershipType") {
    if (!value || value === 0 || value === "0" || value === "None")
      return "None";
    if (value === 1 || value === "1" || value === "Condo") return "Condo";
    return String(value);
  }

  // groupFunctionality: empty array / null / "[]" → "None"
  if (apiKey === "groupFunctionality") {
    if (
      !value ||
      value === "[]" ||
      (Array.isArray(value) && value.length === 0)
    )
      return "None";
    return String(value);
  }

  // feeFrequency: numeric enum or string → capitalize label (Field Rules: None 0, DayOfArrival 1, Daily 2)
  if (apiKey === "feeFrequency") {
    if (value === null || value === undefined || value === "") return "None";
    if (
      typeof value === "number" ||
      (typeof value === "string" && !isNaN(Number(value)))
    ) {
      return FEE_FREQUENCY_MAP[value as string | number] ?? "None";
    }
    const s = String(value);
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  // giftCardType: falsy → "None"
  if (apiKey === "giftCardType") {
    if (!value || value === false || String(value).toLowerCase() === "false")
      return "None";
    return String(value);
  }

  // postTypeName: UI sends "8967 - Z Tax" → extract name after " - "
  // Also normalises snake_case values like "resort_fee" → "Resort Fee"
  if (apiKey && NAME_AFTER_DASH_FIELDS.has(apiKey)) {
    if (!value || value === 0) return "";
    const str = String(value);
    const idx = str.indexOf(" - ");
    const name = idx >= 0 ? str.substring(idx + 3).trim() : str;
    // snake_case → Title Case
    if (name.includes("_")) {
      return name
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }
    return name;
  }

  // postTypeNumber: UI sends "8968 - Z Room Charge" → integer string
  if (apiKey && NUMBER_REF_FIELDS.has(apiKey)) {
    if (!value || value === 0) return "";
    const str = String(value);
    const idx = str.indexOf(" - ");
    const numStr = idx >= 0 ? str.substring(0, idx).trim() : str;
    const num = parseInt(numStr, 10);
    return isNaN(num) ? numStr : String(num);
  }

  // Single code reference fields — "CODE - Name" or object → extract code
  if (apiKey && CODE_REF_FIELDS.has(apiKey)) {
    if (Array.isArray(value))
      return value.length === 0 ? "" : extractCode(value[0]);
    return extractCode(value);
  }

  // Date fields — format as yyyy-MM-dd
  if (apiKey && DATE_FIELDS.has(apiKey)) {
    if (!value || value === "empty" || value === "") return "";
    const d = new Date(value as string);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return "";
  }

  // phoneRestrictions: string enum, numeric 0 = empty
  if (apiKey === "phoneRestrictions") {
    if (
      !value ||
      value === 0 ||
      value === false ||
      String(value).toLowerCase() === "false"
    )
      return "";
    return String(value);
  }

  // characteristics / postTypeCategory: string enum, strip falsy
  if (apiKey === "characteristics" || apiKey === "postTypeCategory") {
    if (
      !value ||
      value === false ||
      String(value).toLowerCase() === "false" ||
      value === 0
    )
      return "";
    return String(value);
  }

  // roomsForOccupancyTax: "empty / int" — strip falsy
  if (apiKey === "roomsForOccupancyTax") {
    if (!value || value === false || String(value).toLowerCase() === "false")
      return "";
    const n = parseInt(String(value), 10);
    return isNaN(n) ? "" : String(n);
  }

  // Boolean fields (Field Rules Type = Boolean) → "True"/"False"
  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }
  if (
    typeof value === "string" &&
    (value === "True" ||
      value === "False" ||
      value === "true" ||
      value === "false")
  ) {
    if (apiKey && BOOLEAN_FIELDS.has(apiKey)) {
      return value.toLowerCase() === "true" ? "True" : "False";
    }
  }
  if (apiKey && BOOLEAN_FIELDS.has(apiKey)) {
    if (value === 1 || value === "1") return "True";
    if (value === 0 || value === "0" || !value) return "False";
  }

  // Int fields (Field Rules Type = int) → integer string
  if (apiKey && INT_FIELDS.has(apiKey)) {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === false
    )
      return "0";
    const n = parseInt(String(value), 10);
    return isNaN(n) ? "0" : String(n);
  }

  // Decimal fields (Field Rules Type = decimal) → decimal string
  if (apiKey && DECIMAL_FIELDS.has(apiKey)) {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === false
    )
      return "0";
    const n = Number(value);
    return isNaN(n) ? "0" : String(n);
  }

  // Empty-when-falsy string fields → ""
  if (apiKey && EMPTY_WHEN_FALSY_FIELDS.has(apiKey)) {
    if (
      !value ||
      value === false ||
      value === 0 ||
      String(value).toLowerCase() === "false"
    )
      return "";
    return String(value);
  }

  // String value for an array field with code extraction
  if (typeof value === "string" && apiKey && ARRAY_EXTRACT_FIELDS[apiKey]) {
    if (!value.trim()) return "[]";
    const code = extractCode(value);
    return code ? `[${code}]` : "[]";
  }

  // Array fields — extract specific property
  if (Array.isArray(value)) {
    if (apiKey && ARRAY_EXTRACT_FIELDS[apiKey]) {
      const prop = ARRAY_EXTRACT_FIELDS[apiKey];
      const extracted = value
        .map((item) => {
          if (!item) return null;
          if (typeof item === "object" && prop in (item as object))
            return (item as Record<string, unknown>)[prop];
          if (typeof item === "string") return extractCode(item);
          return item;
        })
        .filter(Boolean);
      return `[${extracted.join(",")}]`;
    }
    return JSON.stringify(value);
  }

  // Any remaining number → integer string
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : String(value);
  }

  return value;
};

// Fields that should never appear in the API payload (UI-only metadata)
const STRIP_ALWAYS_FIELDS = new Set(["id"]);

const mapRow = (
  screenName: string,
  uiRow: Record<string, unknown>,
): Record<string, unknown> => {
  const renames = FIELD_RENAMES[screenName] ?? {};
  const defaults = FIELD_DEFAULTS[screenName] ?? {};
  const allowedFields = SCREEN_ALLOWED_FIELDS[screenName];

  // Start with serialized defaults so they go through the same type coercions as UI values
  const mapped: Record<string, unknown> = {};
  for (const [defKey, defVal] of Object.entries(defaults)) {
    if (allowedFields && !allowedFields.has(defKey)) continue;
    const serialized = serializeValue(defVal, defKey);
    if (serialized !== null && serialized !== undefined && serialized !== "") {
      mapped[defKey] = serialized;
    }
  }

  // Apply UI values (override defaults)
  for (const [uiKey, value] of Object.entries(uiRow)) {
    if (STRIP_ALWAYS_FIELDS.has(uiKey)) continue;
    const apiKey = renames[uiKey] ?? uiKey;
    if (allowedFields && !allowedFields.has(apiKey)) continue;
    const serialized = serializeValue(value, apiKey);
    if (serialized !== null && serialized !== undefined && serialized !== "") {
      mapped[apiKey] = serialized;
    }
  }
  // Strip any remaining null/undefined/empty and enforce whitelist
  return Object.fromEntries(
    Object.entries(mapped).filter(
      ([k, v]) =>
        v !== null &&
        v !== undefined &&
        v !== "" &&
        (!allowedFields || allowedFields.has(k)),
    ),
  );
};

// ---------------------------------------------------------------------------
// Map all UI rows for a screen into API-shaped rows
// ---------------------------------------------------------------------------
export const mapScreenData = (
  screenName: string,
  uiRows: unknown,
): Record<string, unknown>[] => {
  if (!Array.isArray(uiRows) || uiRows.length === 0) return [];

  const rows = uiRows.map((row) =>
    mapRow(screenName, row as Record<string, unknown>),
  );

  if (screenName !== "Room Definition") {
    return rows;
  }

  return rows.map((row, index) => ({
    ...row,
    listOrder: String(resolveRoomDefinitionListOrder(row.listOrder, index)),
  }));
};

// ---------------------------------------------------------------------------
// Generate a multi-sheet Excel for a group of screens and return base64.
// Screens that share the same sheetKey have their rows merged into one sheet.
// ---------------------------------------------------------------------------
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

export interface GroupExcelResult {
  fileName: string;
  dataUtilityImportData: DataUtilityImportItem[];
  base64: string;
}

// ---------------------------------------------------------------------------
// Generate a multi-sheet Excel for a group and return the data needed for one
// API call: fileName, dataUtilityImportData array (one entry per sheet), and
// the full workbook as base64.
// Screens that share the same sheetKey are merged into one sheet.
// Returns null if no screens in this group have data.
// ---------------------------------------------------------------------------
export const generateGroupExcelBase64 = async (
  group: SettingsGroup,
  payload: Record<string, unknown>,
): Promise<GroupExcelResult | null> => {
  const exportPayload = removeSampleTestFromPayload(payload);
  const sheetMap = new Map<string, Record<string, unknown>[]>();
  for (const screenName of group.screens) {
    const sheetKey = SCREEN_SHEET_KEY[screenName];
    if (!sheetKey) continue;
    const uiRows = exportPayload[screenName];
    const apiRows = mapScreenData(screenName, uiRows);
    if (apiRows.length === 0) continue;
    const existing = sheetMap.get(sheetKey) ?? [];
    sheetMap.set(sheetKey, [...existing, ...apiRows]);
  }

  // Auto-inject Resort Fee Post Type rows referenced by Room Type Maintenance
  if (group.screens.includes("Post Type")) {
    const postTypeSheetKey = SCREEN_SHEET_KEY["Post Type"];
    const rtmRows = Array.isArray(exportPayload["Room Type Maintenance"])
      ? (exportPayload["Room Type Maintenance"] as Record<string, unknown>[])
      : [];
    const existingPostTypes = sheetMap.get(postTypeSheetKey ?? "") ?? [];
    const existingNames = new Set(
      existingPostTypes.map((r) => String(r.postTypeName ?? "").toLowerCase()),
    );
    const toInject: Record<string, unknown>[] = [];
    for (const rtm of rtmRows) {
      const rawName: string = String(rtm.postTypeName ?? "").trim();
      if (!rawName) continue;
      // Normalise "resort_fee" → "Resort Fee"
      const normalised = rawName.includes("_")
        ? rawName
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ")
        : rawName;
      if (!existingNames.has(normalised.toLowerCase())) {
        existingNames.add(normalised.toLowerCase());
        const defaults =
          (FIELD_DEFAULTS["Post Type"] as Record<string, unknown>) ?? {};
        toInject.push({
          ...defaults,
          postTypeName: normalised,
          isActive: "True",
        });
      }
    }
    if (toInject.length > 0 && postTypeSheetKey) {
      sheetMap.set(postTypeSheetKey, [...existingPostTypes, ...toInject]);
    }
  }

  if (sheetMap.size === 0) return null;

  const workbook = new ExcelJS.Workbook();
  const dataUtilityImportData: DataUtilityImportItem[] = [];

  for (const [sheetKey, rows] of Array.from(sheetMap.entries())) {
    const columns = SCREEN_COLUMNS[sheetKey] ?? Object.keys(rows[0] ?? {});
    const worksheet = workbook.addWorksheet(sheetKey);
    worksheet.columns = columns.map((col) => ({
      header: col,
      key: col,
      width: 20,
    }));
    for (const row of rows) {
      worksheet.addRow(row);
    }
    dataUtilityImportData.push({
      importData: JSON.stringify({ [sheetKey]: rows }),
      totalRows: 0,
      excelSheetName: sheetKey,
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const base64 = arrayBufferToBase64(buffer as ArrayBuffer);

  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const fileName = `${group.label}_${mm}-${dd}-${yyyy}_${hh}${min}${ss}.xlsx`;

  return { fileName, dataUtilityImportData, base64 };
};
